// SecurityDialog.tsx
import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogContent,
    Button,
    Switch,
    Box,
    Typography,
    IconButton,
    Fade,
    Slide,
    CircularProgress,
    Chip,
} from '@mui/material';
import apiClient from '../../services/apiClient';

// Import your icons (use whatever icon library you have)
// If you don't have lucide-react, replace with MUI icons:
// import { Shield, Email, Phone, Sms, Close, Check, Lock } from '@mui/icons-material';

type SecurityDialogProps = {
    open: boolean;
    onClose: () => void;
    user: any;
    refreshUsers: () => void;
};

const SecurityDialog: React.FC<SecurityDialogProps> = ({ open, onClose, user, refreshUsers }) => {
    const [is2FA, setIs2FA] = useState(false);
    const [commSms, setCommSms] = useState(false);
    const [commEmail, setCommEmail] = useState(false);
    const [commPhone, setCommPhone] = useState(false);
    const [loading, setLoading] = useState(false);
    const [saveSuccess, setSaveSuccess] = useState(false);

    useEffect(() => {
        if (user) {
            setIs2FA(!!user.is_2fa);
            setCommSms(!!user.communication_channel?.sms);
            setCommEmail(!!user.communication_channel?.email);
            setCommPhone(!!user.communication_channel?.phone);
        }
    }, [user]);

    const handleSave = async () => {
        setLoading(true);
        setSaveSuccess(false);
        try {
            await apiClient(`v1/resource/user/${user.id}`, {
                method: 'PATCH',
                body: {
                    is_2fa: is2FA,
                    communication_channel: {
                        sms: commSms,
                        email: commEmail,
                        phone: commPhone,
                    },
                },
            });
            
            setSaveSuccess(true);
            setTimeout(() => {
                refreshUsers();
                onClose();
            }, 800);
        } catch (err) {
            console.error(err);
            alert('Failed to update settings. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // Icon components - replace with your actual icons
    const ShieldIcon = () => <span style={{fontSize: '24px'}}>🛡️</span>;
    const MessageIcon = () => <span style={{fontSize: '24px'}}>💬</span>;
    const EmailIcon = () => <span style={{fontSize: '24px'}}>📧</span>;
    const PhoneIcon = () => <span style={{fontSize: '24px'}}>📱</span>;
    const LockIcon = () => <span style={{fontSize: '28px'}}>🔒</span>;

    const securityItems = [
        {
            id: '2fa',
            icon: ShieldIcon,
            title: 'Two-Factor Authentication',
            description: 'Add an extra layer of security to your account',
            checked: is2FA,
            onChange: setIs2FA,
            color: '#8b5cf6',
            gradient: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
        },
        {
            id: 'sms',
            icon: MessageIcon,
            title: 'SMS Notifications',
            description: 'Receive security alerts via text message',
            checked: commSms,
            onChange: setCommSms,
            color: '#10b981',
            gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
        },
        {
            id: 'email',
            icon: EmailIcon,
            title: 'Email Notifications',
            description: 'Get updates and alerts via email',
            checked: commEmail,
            onChange: setCommEmail,
            color: '#3b82f6',
            gradient: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
        },
        {
            id: 'phone',
            icon: PhoneIcon,
            title: 'Phone Notifications',
            description: 'Receive important calls about account activity',
            checked: commPhone,
            onChange: setCommPhone,
            color: '#f59e0b',
            gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
        },
    ];

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 4,
                    background: 'linear-gradient(180deg, #ffffff 0%, #f9fafb 100%)',
                    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                    overflow: 'visible',
                },
            }}
            TransitionComponent={Slide}
            TransitionProps={{ direction: 'up' } as any}
        >
            {/* Header with gradient background */}
            <Box
                sx={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    padding: '32px 32px 48px',
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                {/* Decorative blurred circles */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: -50,
                        right: -50,
                        width: 200,
                        height: 200,
                        borderRadius: '50%',
                        background: 'rgba(255, 255, 255, 0.1)',
                        filter: 'blur(40px)',
                    }}
                />
                <Box
                    sx={{
                        position: 'absolute',
                        bottom: -30,
                        left: -30,
                        width: 150,
                        height: 150,
                        borderRadius: '50%',
                        background: 'rgba(255, 255, 255, 0.1)',
                        filter: 'blur(30px)',
                    }}
                />

                <IconButton
                    onClick={onClose}
                    sx={{
                        position: 'absolute',
                        top: 16,
                        right: 16,
                        color: 'white',
                        '&:hover': {
                            background: 'rgba(255, 255, 255, 0.2)',
                            transform: 'rotate(90deg)',
                        },
                        transition: 'all 0.3s ease',
                    }}
                >
                    ✕
                </IconButton>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, position: 'relative' }}>
                    <Box
                        sx={{
                            width: 56,
                            height: 56,
                            borderRadius: 3,
                            background: 'rgba(255, 255, 255, 0.2)',
                            backdropFilter: 'blur(10px)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 8px 16px rgba(0, 0, 0, 0.1)',
                        }}
                    >
                        <LockIcon />
                    </Box>
                    <Box>
                        <Typography
                            variant="h5"
                            sx={{
                                color: 'white',
                                fontWeight: 700,
                                letterSpacing: '-0.5px',
                            }}
                        >
                            Security Settings
                        </Typography>
                        <Typography
                            sx={{
                                color: 'rgba(255, 255, 255, 0.9)',
                                fontSize: '14px',
                                marginTop: '4px',
                            }}
                        >
                            Manage your account security preferences
                        </Typography>
                    </Box>
                </Box>

                {/* {user && (
                    <Chip
                        label={user.email || user.name || 'User'}
                        sx={{
                            position: 'absolute',
                            top: 16,
                            left: 32,
                            background: 'rgba(255, 255, 255, 0.2)',
                            backdropFilter: 'blur(10px)',
                            color: 'white',
                            fontWeight: 600,
                            fontSize: '12px',
                        }}
                    />
                )} */}
            </Box>

            <DialogContent sx={{ padding: 4, paddingTop: 3 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {securityItems.map((item, index) => {
                        const IconComponent = item.icon;
                        return (
                            <Fade in={open} timeout={300 + index * 100} key={item.id}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        padding: 3,
                                        borderRadius: 3,
                                        border: '2px solid',
                                        borderColor: item.checked ? item.color : '#e5e7eb',
                                        background: item.checked
                                            ? `linear-gradient(135deg, ${item.color}08 0%, ${item.color}15 100%)`
                                            : 'white',
                                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                        cursor: 'pointer',
                                        '&:hover': {
                                            borderColor: item.color,
                                            transform: 'translateY(-2px)',
                                            boxShadow: `0 8px 16px ${item.color}25`,
                                        },
                                    }}
                                    onClick={() => item.onChange(!item.checked)}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                                        <Box
                                            sx={{
                                                width: 48,
                                                height: 48,
                                                borderRadius: 2,
                                                background: item.checked ? item.gradient : '#f3f4f6',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                transition: 'all 0.3s ease',
                                                boxShadow: item.checked ? `0 4px 12px ${item.color}40` : 'none',
                                            }}
                                        >
                                            <IconComponent />
                                        </Box>
                                        <Box sx={{ flex: 1 }}>
                                            <Typography
                                                sx={{
                                                    fontWeight: 600,
                                                    fontSize: '15px',
                                                    color: '#111827',
                                                    marginBottom: '2px',
                                                }}
                                            >
                                                {item.title}
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    fontSize: '13px',
                                                    color: '#6b7280',
                                                }}
                                            >
                                                {item.description}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Switch
                                        checked={item.checked}
                                        onChange={(e) => {
                                            e.stopPropagation();
                                            item.onChange(e.target.checked);
                                        }}
                                        sx={{
                                            '& .MuiSwitch-switchBase.Mui-checked': {
                                                color: item.color,
                                            },
                                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                backgroundColor: item.color,
                                            },
                                        }}
                                    />
                                </Box>
                            </Fade>
                        );
                    })}
                </Box>
            </DialogContent>

            {/* Action buttons */}
            <Box
                sx={{
                    padding: '16px 32px 32px',
                    display: 'flex',
                    gap: 2,
                }}
            >
                <Button
                    onClick={onClose}
                    disabled={loading}
                    fullWidth
                    sx={{
                        padding: '12px',
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '15px',
                        border: '2px solid #e5e7eb',
                        color: '#6b7280',
                        '&:hover': {
                            border: '2px solid #d1d5db',
                            background: '#f9fafb',
                        },
                    }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleSave}
                    disabled={loading}
                    fullWidth
                    sx={{
                        padding: '12px',
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '15px',
                        background: saveSuccess
                            ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                            : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white',
                        boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
                        '&:hover': {
                            background: saveSuccess
                                ? 'linear-gradient(135deg, #059669 0%, #047857 100%)'
                                : 'linear-gradient(135deg, #5568d3 0%, #6941a8 100%)',
                            boxShadow: '0 6px 16px rgba(102, 126, 234, 0.5)',
                            transform: 'translateY(-1px)',
                        },
                        '&:disabled': {
                            background: '#e5e7eb',
                            color: '#9ca3af',
                            boxShadow: 'none',
                        },
                        transition: 'all 0.3s ease',
                    }}
                >
                    {loading ? (
                        <CircularProgress size={20} sx={{ color: 'white' }} />
                    ) : saveSuccess ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            ✓ Saved!
                        </Box>
                    ) : (
                        'Save Changes'
                    )}
                </Button>
            </Box>
        </Dialog>
    );
};

export default SecurityDialog;